import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, uploadWithProgress } from "../api";
import StickyHead from "../StickyHead";

/* 포럼(/forum) 글에 첨부하는 파일 저장소를 관리한다.
   파일 실체는 서버의 downloads_files/<카테고리>/ 에 저장되고,
   여기서 제목·설명·버전·공개여부만 붙인다. */

const fmtSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
};

const EMPTY_UP = { category: "hardware", title: "", description: "", version: "", is_public: true, members_only: true };

export default function ResourcesAdmin() {
  const [rows, setRows] = useState(null);
  const [cats, setCats] = useState([]);
  const [limit, setLimit] = useState({ max_bytes: 0, allowed_exts: [] });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [up, setUp] = useState(EMPTY_UP);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(-1); // -1: 대기, 0~100: 업로드 중
  const fileRef = useRef(null);

  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ category: "", title: "", description: "", version: "" });
  const [busy, setBusy] = useState(false);

  async function load() {
    setError("");
    try {
      const [list, meta] = await Promise.all([
        api.get("/resources"),
        api.get("/resources/categories"),
      ]);
      setRows(list);
      setCats(meta.categories || []);
      setLimit({ max_bytes: meta.max_bytes || 0, allowed_exts: meta.allowed_exts || [] });
    } catch (err) {
      setError(err.message);
      setRows([]);
    }
  }

  useEffect(() => { load(); }, []);

  const catDesc = useMemo(
    () => cats.find((c) => c.id === up.category)?.desc || "",
    [cats, up.category]
  );

  const groups = useMemo(() => {
    if (!rows) return [];
    return cats
      .map((c) => ({ ...c, items: rows.filter((r) => r.category === c.id) }))
      .filter((g) => g.items.length > 0);
  }, [rows, cats]);

  const publicCount = rows ? rows.filter((r) => r.is_public).length : 0;
  const totalDown = rows ? rows.reduce((s, r) => s + (r.download_count || 0), 0) : 0;

  function pickFile(e) {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setError("");
    // 제목을 아직 안 적었으면 파일명에서 자동으로 채운다
    if (f && !up.title.trim()) {
      setUp((u) => ({ ...u, title: f.name.replace(/\.[^.]+$/, "") }));
    }
  }

  async function submitUpload(e) {
    e.preventDefault();
    if (!file) { setError("올릴 파일을 선택하세요."); return; }
    if (limit.max_bytes && file.size > limit.max_bytes) {
      setError(`파일이 너무 큽니다. (최대 ${fmtSize(limit.max_bytes)})`);
      return;
    }

    setError(""); setNotice(""); setProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", up.category);
    fd.append("title", up.title);
    fd.append("description", up.description);
    fd.append("version", up.version);
    fd.append("is_public", up.is_public ? "true" : "false");
    fd.append("members_only", up.members_only ? "true" : "false");

    try {
      await uploadWithProgress("/resources", fd, setProgress);
      setUp(EMPTY_UP);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setNotice("업로드했습니다. 포럼 관리에서 글에 첨부하면 홈페이지에 보입니다.");
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setProgress(-1);
    }
  }

  /* 메타 행이 없는 파일(SSH 직접 투입분)에 행을 만들고 id 를 돌려준다 */
  async function ensureRow(row) {
    if (row.id != null) return row.id;
    const fd = new FormData();
    fd.append("rel_path", row.rel_path);
    const created = await uploadWithProgress("/resources/register", fd, null);
    return created.id;
  }

  async function togglePublic(row) {
    setError(""); setBusy(true);
    try {
      const id = await ensureRow(row);
      await api.patch(`/resources/${id}`, { is_public: !row.is_public });
      await load();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function toggleMembersOnly(row) {
    setError(""); setBusy(true);
    try {
      const id = await ensureRow(row);
      await api.patch(`/resources/${id}`, { members_only: !row.members_only });
      await load();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  function startEdit(row) {
    setEditId(row.id ?? `new:${row.rel_path}`);
    setEdit({ category: row.category, title: row.title || "", description: row.description || "", version: row.version || "" });
    setError("");
  }

  async function saveEdit(row) {
    setError(""); setBusy(true);
    try {
      const id = await ensureRow(row);
      await api.patch(`/resources/${id}`, {
        category: edit.category,
        title: edit.title,
        description: edit.description,
        version: edit.version,
      });
      setEditId(null);
      await load();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function remove(row) {
    if (row.id == null) {
      setError("서버에 직접 넣은 파일입니다. 먼저 '목록에 등록'한 뒤 삭제하세요.");
      return;
    }
    if (!window.confirm(`'${row.title}' 자료를 삭제합니다.\n파일도 서버에서 함께 지워집니다. 계속할까요?`)) {
      return;
    }
    setError(""); setBusy(true);
    try {
      await api.del(`/resources/${row.id}`);
      await load();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  const uploading = progress >= 0;

  return (
    <div>
      <StickyHead>
        <div style={{ marginBottom: 12 }}>
          <Link to="/sstoa" className="sstoa-back">← 홈</Link>
        </div>

        <div className="sstoa-topbar">
          <div>
            <h1 className="sstoa-title">포럼 첨부파일</h1>
            <p className="sstoa-sub">
              포럼 글에 첨부하는 파일 저장소입니다. 여기에 올린 파일은{" "}
              <Link className="sstoa-link" to="/sstoa/forum">포럼 관리</Link>
              에서 글에 첨부해야 홈페이지에 보입니다. 제목 · 버전 · 회원 전용 · 공개 여부는 여기서 바꿉니다.
            </p>
          </div>
        </div>
      </StickyHead>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}
      {notice && <div className="sstoa-notice">{notice}</div>}

      {rows && rows.length > 0 && (
        <div className="sstoa-stats">
          <div className="sstoa-stat">
            <div className="sstoa-stat__num">{rows.length}</div>
            <div className="sstoa-stat__label">전체 자료</div>
          </div>
          <div className="sstoa-stat">
            <div className="sstoa-stat__num">{publicCount}</div>
            <div className="sstoa-stat__label">공개 중</div>
          </div>
          <div className="sstoa-stat">
            <div className="sstoa-stat__num">{totalDown.toLocaleString("ko-KR")}</div>
            <div className="sstoa-stat__label">누적 다운로드</div>
          </div>
        </div>
      )}

      {/* ---------------- 업로드 ---------------- */}
      <div className="sstoa-stack">
      <section className="sstoa-panel sstoa-panel--pad">
        <h2 className="sstoa-subpanel__h" style={{ fontSize: 15, margin: "0 0 14px" }}>새 자료 올리기</h2>

        <form className="sstoa-form" onSubmit={submitUpload}>
          <div className="sstoa-form-cols">
            <label className="sstoa-field">
              <span>
                분류
                {catDesc && <small> — {catDesc}</small>}
              </span>
              <select
                className="sstoa-input"
                value={up.category}
                onChange={(e) => setUp((u) => ({ ...u, category: e.target.value }))}
                disabled={uploading}
              >
                {(cats.length ? cats : [{ id: "hardware", label: "하드웨어 자료" }]).map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </label>

            <label className="sstoa-field">
              <span>버전 <small>(선택)</small></span>
              <input
                className="sstoa-input" type="text" placeholder="1.0.0" maxLength={40}
                value={up.version}
                onChange={(e) => setUp((u) => ({ ...u, version: e.target.value }))}
                disabled={uploading}
              />
            </label>
          </div>

          <label className="sstoa-field">
            <span>파일</span>
            <input
              ref={fileRef} className="sstoa-input" type="file"
              onChange={pickFile} disabled={uploading}
            />
          </label>
          {file && (
            <p className="sstoa-hint">
              {file.name} · {fmtSize(file.size)}
              {limit.max_bytes ? ` (최대 ${fmtSize(limit.max_bytes)})` : ""}
            </p>
          )}

          <label className="sstoa-field">
            <span>제목</span>
            <input
              className="sstoa-input" type="text" maxLength={200}
              placeholder="첨부 자료에 표시될 이름"
              value={up.title}
              onChange={(e) => setUp((u) => ({ ...u, title: e.target.value }))}
              disabled={uploading}
            />
          </label>

          <label className="sstoa-field">
            <span>설명 <small>(선택)</small></span>
            <textarea
              className="sstoa-textarea" rows={3}
              placeholder="어떤 자료인지 한두 줄로 적어주세요."
              value={up.description}
              onChange={(e) => setUp((u) => ({ ...u, description: e.target.value }))}
              disabled={uploading}
            />
          </label>

          <label className="sstoa-check">
            <input
              type="checkbox" checked={up.is_public}
              onChange={(e) => setUp((u) => ({ ...u, is_public: e.target.checked }))}
              disabled={uploading}
            />
            <span>바로 공개 (끄면 홈페이지에 나오지 않습니다)</span>
          </label>

          <label className="sstoa-check">
            <input
              type="checkbox" checked={up.members_only}
              onChange={(e) => setUp((u) => ({ ...u, members_only: e.target.checked }))}
              disabled={busy}
            />
            <span>회원 전용 (끄면 로그인 없이도 받을 수 있습니다)</span>
          </label>

          {uploading && (
            <div className="sstoa-progress">
              <div className="sstoa-progress__bar" style={{ width: `${progress}%` }} />
              <span className="sstoa-progress__label">{progress}%</span>
            </div>
          )}

          <div className="sstoa-actions">
            <button type="submit" className="sstoa-btn sstoa-btn--primary" disabled={uploading || busy}>
              {uploading ? "올리는 중…" : "업로드"}
            </button>
          </div>
        </form>
      </section>

      {/* ---------------- 목록 ---------------- */}
      {rows === null && <div className="sstoa-empty">불러오는 중…</div>}

      {rows !== null && rows.length === 0 && (
        <div className="sstoa-empty">
          아직 올린 자료가 없습니다. 위에서 첫 파일을 올려보세요.
        </div>
      )}

      {groups.map((g) => (
        <section key={g.id} className="sstoa-panel">
          <div className="sstoa-panel__head">
            <h2>{g.label} <span className="sstoa-tab__cnt">{g.items.length}</span></h2>
          </div>

          <table className="sstoa-table">
            <thead>
              <tr>
                <th>제목</th>
                <th>파일</th>
                <th>크기</th>
                <th>다운로드</th>
                <th>공개</th>
                <th>다운로드 권한</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {g.items.map((r) => {
                const key = r.id ?? `new:${r.rel_path}`;
                const editing = editId === key;
                return (
                  <tr key={r.rel_path}>
                    <td>
                      {editing ? (
                        <div className="sstoa-editcell">
                          <select
                            className="sstoa-input" value={edit.category}
                            onChange={(e) => setEdit((s) => ({ ...s, category: e.target.value }))}
                            title="분류를 바꾸면 파일이 해당 분류로 옮겨집니다"
                          >
                            {cats.map((c) => (
                              <option key={c.id} value={c.id}>{c.label}</option>
                            ))}
                          </select>
                          <input
                            className="sstoa-input" type="text" value={edit.title}
                            onChange={(e) => setEdit((s) => ({ ...s, title: e.target.value }))}
                            placeholder="제목"
                          />
                          <input
                            className="sstoa-input" type="text" value={edit.version}
                            onChange={(e) => setEdit((s) => ({ ...s, version: e.target.value }))}
                            placeholder="버전 (선택)"
                          />
                          <textarea
                            className="sstoa-textarea" rows={2} value={edit.description}
                            onChange={(e) => setEdit((s) => ({ ...s, description: e.target.value }))}
                            placeholder="설명 (선택)"
                          />
                        </div>
                      ) : (
                        <>
                          <strong>{r.title}</strong>
                          {r.version && <span className="sstoa-tag">v{r.version}</span>}
                          {r.id == null && <span className="sstoa-tag sstoa-tag--gray">미등록</span>}
                          {r.description && <div className="sstoa-rowdesc">{r.description}</div>}
                        </>
                      )}
                    </td>
                    <td className="sstoa-mono">{r.filename}</td>
                    <td>{fmtSize(r.size)}</td>
                    <td>{(r.download_count || 0).toLocaleString("ko-KR")}회</td>
                    <td>
                      <button
                        type="button"
                        className={`sstoa-toggle ${r.is_public ? "is-on" : ""}`}
                        onClick={() => togglePublic(r)}
                        disabled={busy}
                        title={r.is_public ? "공개 중 — 누르면 숨김" : "숨김 — 누르면 공개"}
                      >
                        {r.is_public ? "공개" : "숨김"}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`sstoa-toggle ${r.members_only ? "is-on" : ""}`}
                        onClick={() => toggleMembersOnly(r)}
                        disabled={busy}
                        title={r.members_only ? "회원만 내려받을 수 있음 — 누르면 누구나 가능" : "누구나 내려받을 수 있음 — 누르면 회원 전용"}
                      >
                        {r.members_only ? "회원 전용" : "전체 공개"}
                      </button>
                    </td>
                    <td className="sstoa-actions-cell">
                      {editing ? (
                        <>
                          <button type="button" className="sstoa-btn sstoa-btn--sm sstoa-btn--primary"
                            onClick={() => saveEdit(r)} disabled={busy}>저장</button>
                          <button type="button" className="sstoa-btn sstoa-btn--sm"
                            onClick={() => setEditId(null)} disabled={busy}>취소</button>
                        </>
                      ) : (
                        <>
                          <a className="sstoa-btn sstoa-btn--sm" href={r.url} target="_blank" rel="noreferrer">받기</a>
                          <button type="button" className="sstoa-btn sstoa-btn--sm"
                            onClick={() => startEdit(r)} disabled={busy}>수정</button>
                          <button type="button" className="sstoa-btn sstoa-btn--sm sstoa-btn--danger"
                            onClick={() => remove(r)} disabled={busy}>삭제</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ))}
      </div>

      {rows !== null && rows.some((r) => r.id == null) && (
        <p className="sstoa-hint">
          '미등록'은 서버 폴더에 직접 넣은 파일입니다. 공개는 되고 있으며,
          제목·설명을 붙이려면 수정을 누르면 목록에 등록됩니다.
        </p>
      )}
    </div>
  );
}
